import { useQuery } from "@tanstack/react-query";

interface Landmark {
  featureText: string | undefined;
  locality: string | undefined;
  place: string | undefined;
  district: string | undefined;
  region: string | undefined;
}

const fetchLandmark = async (
  lat: number,
  lng: number,
  accessToken: string
): Promise<Landmark | undefined> => {
  const url = `https://api.mapbox.com/geocoding/v5/mapbox.places/${lng},${lat}.json?access_token=${accessToken}`;
  const response = await fetch(url);

  if (!response.ok) {
    throw new Error(`Error fetching place name: ${response.statusText}`);
  }

  const data = await response.json();

  if (data.features && data.features.length > 0) {
    const firstFeature = data.features[0];
    if (firstFeature.context) {
      const context = firstFeature.context as any[];
      return {
        featureText: firstFeature.text,
        locality: context.find((c) => c.id.includes("locality"))?.text,
        place: context.find((c) => c.id.includes("place"))?.text,
        district: context.find((c) => c.id.includes("district"))?.text,
        region: context.find((c) => c.id.includes("region"))?.text,
      };
    }
  }

  return undefined;
};

export const useReverseGeocoder = (
  lat: number | undefined,
  lng: number | undefined
) => {
  const accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN ?? "";

  const queryFn = async (): Promise<Landmark | undefined> => {
    if (lat === undefined || lng === undefined) {
      return undefined;
    }
    return fetchLandmark(lat, lng, accessToken);
  };

  const queryKey = ["reverseGeocoder", lat, lng];

  const {
    data: landmark,
    error,
    isLoading,
  } = useQuery({
    queryKey,
    queryFn,
    enabled: lat !== undefined && lng !== undefined,
  });

  return { landmark, error, isLoading };
};

export default useReverseGeocoder;
